import { ColumnDef } from "@tanstack/react-table";
import { Button } from "../components/ui/button";
import { ArrowUpDown, MoreHorizontal, Edit, Trash2, Eye } from "lucide-react";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "../components/ui/dropdown-menu";
import { useNavigate } from "react-router-dom";
import { useState } from "react";
import { ConfirmationDialog } from "../components/confirmationDialog";
import axios from "axios";

export type Client = {
  id: string;
  clientName: string;
  contactPerson: {
    name: string;
    email: string;
    phone?: string;
  };
  company: string;
  status: "active" | "inactive" | "onhold";
  gstNumber: string;
  phoneNumber: string;
  dateOfOnboarding: string;
  address: {
    city: string;
    state: string;
  };
  totalProjects?: number;
  activeProjects?: number;
};

// Status badge component - using inline span instead of Badge component
const StatusBadge = ({ status }: { status: Client["status"] }) => {
  const getStatusColor = (status: string) => {
    switch (status) {
      case "active":
        return "bg-green-100 text-green-800 border border-green-200";
      case "inactive":
        return "bg-gray-100 text-gray-800 border border-gray-200";
      case "onhold":
        return "bg-yellow-100 text-yellow-800 border border-yellow-200";
      default:
        return "bg-gray-100 text-gray-800 border border-gray-200";
    }
  };

  return (
    <span
      className={`inline-flex items-center px-2 py-1 text-xs font-medium rounded-md border capitalize ${getStatusColor(
        status
      )}`}
    >
      {status === "onhold" ? "On Hold" : status}
    </span>
  );
};

// Actions component
const ClientActionsCell = ({
  client,
  onClientDeleted,
}: {
  client: Client;
  onClientDeleted: () => void;
}) => {
  const navigate = useNavigate();
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);

  const handleView = () => {
    navigate(`/clients/${client.id}`);
  };

  const handleEdit = () => {
    navigate(`/clients/edit/${client.id}`);
  };

  const handleDelete = async () => {
    try {
      setIsDeleting(true);
      const token = localStorage.getItem("authToken");

      await axios.delete(
        `${process.env.REACT_APP_BACKEND_URL}api/clients/${client.id}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      setIsDeleteDialogOpen(false);
      onClientDeleted();
    } catch (error: any) {
      console.error("Error deleting client:", error);
      alert(
        error.response?.data?.message ||
          "Failed to delete client. Please try again."
      );
    } finally {
      setIsDeleting(false);
    }
  };

  return (
    <>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button variant="ghost" className="h-8 w-8 p-0">
            <span className="sr-only">Open menu</span>
            <MoreHorizontal className="h-4 w-4" />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end">
          <DropdownMenuLabel>Actions</DropdownMenuLabel>
          <DropdownMenuItem onClick={handleEdit}>
            <Edit className="mr-2 h-4 w-4" />
            Edit Client
          </DropdownMenuItem>
          <DropdownMenuSeparator />
          <DropdownMenuItem
            onClick={() => setIsDeleteDialogOpen(true)}
            className="text-red-600"
          >
            <Trash2 className="mr-2 h-4 w-4" />
            Delete Client
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>

      <ConfirmationDialog
        isOpen={isDeleteDialogOpen}
        onClose={() => setIsDeleteDialogOpen(false)}
        title="Delete Client"
        description={`Are you sure you want to delete "${client.clientName}"? This action cannot be undone.`}
        onConfirm={handleDelete}
        isLoading={isDeleting}
        confirmText="Delete"
        cancelText="Cancel"
        isDestructive={true}
      />
    </>
  );
};

export const createClientColumns = (
  onClientDeleted: () => void
): ColumnDef<Client>[] => [
  {
    accessorKey: "clientName",
    header: ({ column }) => {
      return (
        <Button
          variant="ghost"
          onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}
          className="h-8 px-2 lg:px-3"
        >
          Client Name
          <ArrowUpDown className="ml-2 h-4 w-4" />
        </Button>
      );
    },
    cell: ({ row }) => {
      const client = row.original;
      return (
        <div className="flex flex-col">
          <span className="font-medium">{client.clientName}</span>
          <span className="text-sm text-muted-foreground">
            {client.gstNumber}
          </span>
        </div>
      );
    },
  },
  {
    accessorKey: "contactPerson",
    header: "Contact Person",
    cell: ({ row }) => {
      const contact = row.getValue("contactPerson") as Client["contactPerson"];
      return (
        <div className="flex flex-col">
          <span className="font-medium">{contact.name}</span>
          <span className="text-sm text-muted-foreground">{contact.email}</span>
        </div>
      );
    },
  },
  {
    accessorKey: "company",
    header: "Company",
    cell: ({ row }) => {
      const company = row.getValue("company") as string;
      return (
        <div className="text-sm">
          {company === "Bot2Do Technologies Pvt. Ltd" ? "Bot2Do" : "DevAnsh"}
        </div>
      );
    },
  },
  {
    accessorKey: "status",
    header: "Status",
    cell: ({ row }) => {
      return <StatusBadge status={row.getValue("status")} />;
    },
  },
  {
    accessorKey: "dateOfOnboarding",
    header: ({ column }) => {
      return (
        <Button
          variant="ghost"
          onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}
          className="h-8 px-2 lg:px-3"
        >
          Onboarded
          <ArrowUpDown className="ml-2 h-4 w-4" />
        </Button>
      );
    },
    cell: ({ row }) => {
      const date = new Date(row.getValue("dateOfOnboarding"));
      return <div className="text-sm">{date.toLocaleDateString()}</div>;
    },
  },
  {
    accessorKey: "projects",
    header: "Projects",
    cell: ({ row }) => {
      const client = row.original;
      return (
        <div className="text-sm">
          <span className="font-medium">{client.totalProjects || 0}</span>
          <span className="text-muted-foreground"> total</span>
          {client.activeProjects ? (
            <div className="text-xs text-green-600">
              {client.activeProjects} active
            </div>
          ) : null}
        </div>
      );
    },
  },
  {
    id: "actions",
    enableHiding: false,
    cell: ({ row }) => {
      const client = row.original;
      return (
        <ClientActionsCell client={client} onClientDeleted={onClientDeleted} />
      );
    },
  },
];
